import {
  Component,
  OnInit,
  Inject,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule,
} from '@angular/forms';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Clipboard } from '@angular/cdk/clipboard';
import { ConsentService } from '@services/consent.service';
import { UtilService } from '@services/util.service';
import { LogService } from '@services/log.service';
import { Partner } from '@models/partner.model';
import { Meter } from '@models/meter.model';
import {
  Observable,
  of,
  catchError,
  switchMap,
  tap,
  firstValueFrom,
} from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
  ConsentRequest,
  RelationshipType,
  NotificationStatus,
} from '@models/consent.model';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { NotificationService } from '@services/notification.service';
import { SdkManagerService } from '@services/sdk-manager.service';

export interface SharePartnerDialogData {
  meters: Meter[]; // Changed from single meter to array of meters
  requestId?: string; // Request ID for the sharing operation
  organization?: Partner; // Optional pre-selected organization
  isNewlyAdded: boolean;
  consentRequest?: ConsentRequest;
  hasSkippedMeters?: boolean; // Flag to indicate if there are skipped meters
}

type SharingOption = 'platform' | 'email' | 'link';

@Component({
  selector: 'app-share-partner-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatRadioModule,
    MatTooltipModule,
  ],
  templateUrl: './share-partner-dialog.component.html',
  styleUrls: ['./share-partner-dialog.component.scss'],
  animations: [
    trigger('slideDown', [
      state(
        'void',
        style({
          height: '0px',
          opacity: 0,
          overflow: 'hidden',
          paddingTop: '0',
          paddingBottom: '0',
          marginTop: '0',
          marginBottom: '0',
        }),
      ),
      transition(':enter', [
        animate(
          '300ms ease-out',
          style({
            height: '*',
            opacity: 1,
            paddingTop: '*',
            paddingBottom: '*',
            marginTop: '*',
            marginBottom: '*',
          }),
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms ease-in',
          style({
            height: '0px',
            opacity: 0,
            paddingTop: '0',
            paddingBottom: '0',
            marginTop: '0',
            marginBottom: '0',
          }),
        ),
      ]),
    ]),
  ],
})
export class SharePartnerDialogComponent implements OnInit {
  notificationService = inject(NotificationService);
  sdkManagerService = inject(SdkManagerService);

  isSdkMode = this.sdkManagerService.isSessionActive; // Flag to indicate if this is an SDK request

  searchForm: FormGroup;
  emailForm: FormGroup;
  selectedSharingOption: SharingOption | null = null; // Default to null, will be set based on context
  isLoading = false;
  isSharing = false;
  isGeneratingLink = false;
  isSendingToEmail = false;
  selectedPartner: Partner | null = null;
  validOrganizations: Partner[] = [];
  fetchingOrganizations = false;
  isNewlyAdded: boolean = false;
  isAddingNewPlatform = false; // New property for add platform mode
  isAddingOrganization = false; // New property for loading state when adding org

  // Consent request mode properties
  isConsentRequestMode = false;

  @ViewChild('dialogContent') dialogContent!: ElementRef;

  // Computed properties for handling single vs multiple meters
  get isSingleMeter(): boolean {
    return this.data.meters.length === 1;
  }

  get primaryMeter(): Meter {
    return this.data.meters[0];
  }

  get meterDisplayText(): string {
    return this.isSingleMeter
      ? this.primaryMeter.meterNumber
      : `${this.data.meters.length} meters`;
  }

  get meterNumbers(): string[] {
    return this.data.meters.map((m) => m.meterNumber);
  }

  get hasNonOccupierMeters(): boolean {
    return this.data.meters.some(
      (m) =>
        m.relationship !== RelationshipType.OCCUPIER &&
        m.relationship !== RelationshipType.BILL_PAYER,
    );
  }

  constructor(
    private fb: FormBuilder,
    private consentService: ConsentService,
    private utilService: UtilService,
    private clipboard: Clipboard,
    private logService: LogService,
    public dialogRef: MatDialogRef<SharePartnerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SharePartnerDialogData,
  ) {
    this.searchForm = this.fb.group({
      organization: ['', [Validators.required]],
      newPlatformName: [''], // New form control for adding platform
    });
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
    if (this.hasNonOccupierMeters || this.isSdkMode) {
      this.selectedSharingOption = 'platform';
    }

    // Set consent request mode
    this.isConsentRequestMode = !!this.data?.consentRequest;
  }

  ngOnInit(): void {
    // Log page view with dialog data
    this.logService.logPageView('share-partner-dialog', this.data);

    // Load all organizations on component initialization
    this.loadOrganizations();

    // If an organization was provided, select it
    if (this.data && this.data.organization) {
      this.isLoading = true;
      of(this.data.organization)
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe((partner) => {
          if (partner) {
            // Set the form value and trigger partner selection
            this.searchForm.get('organization')?.setValue(partner);
            this.onOrganizationSelected(partner);
          }
        });
    }

    if (this.data?.isNewlyAdded) {
      this.isNewlyAdded = this.data.isNewlyAdded;
    }
  }

  private setPreferredOrganization(): void {
    // Auto-select the preferred organization if one exists
    const preferredOrg = this.validOrganizations.find((org) => org.isPreferred);
    if (preferredOrg) {
      this.selectedSharingOption = 'platform';
      this.searchForm.get('organization')?.setValue(preferredOrg);
      this.onOrganizationSelected(preferredOrg);
    }
  }

  private loadOrganizations(): void {
    this.fetchingOrganizations = true;

    // Get all organizations (already sorted by service)
    this.consentService
      .getAllOrganizations()
      .pipe(finalize(() => (this.fetchingOrganizations = false)))
      .subscribe({
        next: (organizations) => {
          // Organizations are already sorted by the service
          this.validOrganizations = organizations;

          this.setPreferredOrganization();
        },
        error: (error) => {
          console.error('Error fetching organizations:', error);
          this.validOrganizations = [];
        },
      });
  }

  onOrganizationSelected(selectedOrg: Partner): void {
    this.selectedPartner = selectedOrg;

    // Smooth scroll to the end of the dialog
    this.scrollToEndOfDialog();
  }

  /**
   * Scrolls to the end of the dialog content with smooth animation
   */
  private scrollToEndOfDialog(): void {
    if (this.dialogContent?.nativeElement) {
      const element = this.dialogContent.nativeElement;
      // Use setTimeout to ensure DOM has been updated before scrolling
      setTimeout(() => {
        element.scrollTo({
          top: element.scrollHeight,
          behavior: 'smooth',
        });
      }, 300);
    }
  }

  onSharingOptionChange(value?: string): void {
    // If value is provided, update selectedSharingOption
    if (value) {
      this.selectedSharingOption = value as SharingOption;
    }

    // Reset selections when changing sharing option
    this.selectedPartner = null;
    this.searchForm.reset();
    this.emailForm.reset();
    this.isAddingNewPlatform = false; // Reset add platform mode

    // Set form validators based on selected option
    if (this.selectedSharingOption === 'platform') {
      this.searchForm.get('organization')?.setValidators([Validators.required]);
      this.emailForm.get('email')?.clearValidators();
      this.setPreferredOrganization();
    } else if (this.selectedSharingOption === 'email') {
      this.emailForm
        .get('email')
        ?.setValidators([Validators.required, Validators.email]);
      this.searchForm.get('organization')?.clearValidators();
    } else {
      // For 'link' option, no specific validators needed
      this.searchForm.get('organization')?.clearValidators();
      this.emailForm.get('email')?.clearValidators();
    }

    // Update form validity
    this.searchForm.get('organization')?.updateValueAndValidity();
    this.emailForm.get('email')?.updateValueAndValidity();
  }

  // Method to clear selected platform
  clearSelectedPlatform(): void {
    this.selectedPartner = null;
    this.searchForm.get('organization')?.reset();
  }

  // Method to switch to add new platform mode
  switchToAddPlatformMode(): void {
    this.isAddingNewPlatform = true;
    this.selectedPartner = null;
    this.searchForm.get('organization')?.reset();
    this.searchForm
      .get('newPlatformName')
      ?.setValidators([Validators.required]);
    this.searchForm.get('newPlatformName')?.updateValueAndValidity();
  }

  // Method to cancel add platform mode
  cancelAddPlatform(): void {
    this.isAddingNewPlatform = false;
    this.searchForm.get('newPlatformName')?.reset();
    this.searchForm.get('newPlatformName')?.clearValidators();
    this.searchForm.get('newPlatformName')?.updateValueAndValidity();
  }

  // Method to add new platform
  addNewPlatform(): void {
    const platformName = this.searchForm.get('newPlatformName')?.value?.trim();
    if (!platformName) {
      return;
    }

    // Create a partner object for the new platform
    const newPartner: Partner = {
      name: platformName,
      software: platformName, // Use the same name for software
    };

    this.logService.addShareActionLog('ADD_PLATFORM', { platformName });

    // Add to valid organizations list at the top
    this.validOrganizations.unshift(newPartner);

    // Select the new platform
    this.selectedPartner = newPartner;
    this.searchForm.get('organization')?.setValue(newPartner);

    // Exit add platform mode
    this.cancelAddPlatform();

    // Scroll to show the selected platform
    this.scrollToEndOfDialog();

    this.utilService.openSnackBar(
      `Platform "${platformName}" added to the list. Share to complete the setup.`,
      'Success',
      'success',
    );
  }

  shareWithPartner(): void {
    // If in add platform mode, require user to add the platform first
    if (this.isAddingNewPlatform) {
      this.utilService.openSnackBar(
        'Please add the platform first or cancel to select from existing platforms.',
        'Info',
        'success',
      );
      return;
    }

    const currentValue = this.searchForm.get('organization')?.value;

    // If no value selected, return early
    if (!currentValue) {
      return;
    }

    // Since we're using mat-select, the value should always be a Partner object
    this.selectedPartner = currentValue;
    this.shareWithExistingPartner();
  }

  private shareWithExistingPartner(): void {
    if (!this.selectedPartner) {
      this.isSharing = false;
      return;
    }

    this.isSharing = true;

    // Use the provided consentRequestId or fall back to the one from data
    let consentRequestId = undefined;
    if (this.data.consentRequest?.agentOrg === this.selectedPartner.name) {
      consentRequestId = this.data.consentRequest.consentRequestId;
    }

    this.logService.addShareActionLog('PLATFORM_SHARE', {
      platform: this.selectedPartner,
      meters: this.data.meters,
      consentRequestId: consentRequestId ?? 'NOT_AVAILABLE',
    });

    this.consentService
      .shareMeterWithPartner(
        this.data.meters,
        this.selectedPartner,
        this.data.requestId,
        consentRequestId,
      )
      .pipe(finalize(() => (this.isSharing = false)))
      .subscribe({
        next: (shareId) => {
          if (shareId) {
            this.dialogRef.close({
              shared: true,
              partner: this.selectedPartner,
            });
            if (consentRequestId) {
              this.sdkManagerService.messageAndTerminateSession({
                status: 'SUCCESS',
                message: consentRequestId,
              });
            } else {
              this.sdkManagerService.messageAndTerminateSession({
                status: 'FAILURE',
                message: 'SHARED_WITH_OTHER_PLATFORM',
              });
            }
          } else {
            this.utilService.openSnackBar(
              'Failed to share meters with the selected partner. Please try again.',
              'Error',
              'error',
            );
            this.sdkManagerService.messageAndTerminateSession({
              status: 'FAILURE',
              message: 'INTERNAL_FAILURE_SHARE',
            });
          }
        },
        error: (error) => {
          this.utilService.showErrorMessageFromAPIResponse(
            'Failed to share meters.',
            error,
          );
          this.sdkManagerService.messageAndTerminateSession({
            status: 'FAILURE',
            message: 'INTERNAL_FAILURE_SHARE',
          });
        },
      });
  }

  copyShareableLink(): void {
    this.isGeneratingLink = true;

    let consentRequestId = this.data.consentRequest?.consentRequestId;
    if (this.data.hasSkippedMeters) {
      // Consent altered, set consentRequestId to undefined
      consentRequestId = undefined;
    }

    this.logService.addShareActionLog('LINK_SHARE', {
      meters: this.data.meters,
      consentRequestId: consentRequestId ?? 'NOT_AVAILABLE',
    });

    this.consentService
      .generateShareableLink(
        'link',
        this.data.meters.map((m) => m.meterNumber),
        this.data.requestId,
        consentRequestId,
      )
      .pipe(finalize(() => (this.isGeneratingLink = false)))
      .subscribe({
        next: (link) => {
          this.clipboard.copy(link);
          this.utilService.openSnackBar(
            'Shareable link copied to clipboard!',
            'Success',
            'success',
          );
          this.dialogRef.close();
          this.sdkManagerService.messageAndTerminateSession({
            status: 'FAILURE',
            message: 'SHARED_VIA_EMAIL_OR_LINK',
          });
        },
        error: (error) => {
          console.error('Error generating link:', error);
          this.utilService.openSnackBar(
            'Failed to generate shareable link. Please try again.',
            'Error',
            'error',
          );
          this.sdkManagerService.messageAndTerminateSession({
            status: 'FAILURE',
            message: 'INTERNAL_FAILURE_SHARE',
          });
        },
      });
  }

  sendDataToEmail(): void {
    if (this.emailForm.invalid) {
      return;
    }

    const email = this.emailForm.get('email')?.value;
    if (!email) {
      return;
    }

    this.isSendingToEmail = true;

    let consentRequestId = this.data.consentRequest?.consentRequestId;
    if (this.data.hasSkippedMeters) {
      // Consent altered, set consentRequestId to undefined
      consentRequestId = undefined;
    }
    this.logService.addShareActionLog('EMAIL_SHARE', {
      email,
      meters: this.data.meters,
      consentRequestId: consentRequestId ?? 'NOT_AVAILABLE',
    });

    // First generate the shareable link to get the shareId
    this.consentService
      .generateShareableLink(
        'email',
        this.data.meters.map((m) => m.meterNumber),
        this.data.requestId,
        consentRequestId,
      )
      .pipe(
        switchMap((link) => {
          // Extract shareId from the generated link
          const urlParams = new URL(link).searchParams;
          const shareId = urlParams.get('token');

          if (!shareId) {
            throw new Error('Failed to extract share ID from generated link');
          }

          // Call the data share API with shareId and email
          return this.consentService.shareDataToEmail(shareId, email);
        }),
        finalize(() => (this.isSendingToEmail = false)),
      )
      .subscribe({
        next: (success) => {
          if (success) {
            this.utilService.openSnackBar(
              `Data will be sent to ${email} shortly!`,
              'Success',
              'success',
            );
            this.emailForm.reset();
            this.dialogRef.close();
            this.sdkManagerService.messageAndTerminateSession({
              status: 'FAILURE',
              message: 'SHARED_VIA_EMAIL_OR_LINK',
            });
          } else {
            this.utilService.openSnackBar(
              'Failed to send data to email. Please try again.',
              'Error',
              'error',
            );
            this.sdkManagerService.messageAndTerminateSession({
              status: 'FAILURE',
              message: 'INTERNAL_FAILURE_SHARE',
            });
          }
        },
        error: (error) => {
          console.error('Error sending data to email:', error);
          this.utilService.openSnackBar(
            'Failed to send data to email. Please try again.',
            'Error',
            'error',
          );
          this.sdkManagerService.messageAndTerminateSession({
            status: 'FAILURE',
            message: 'INTERNAL_FAILURE_SHARE',
          });
        },
      });
  }

  onCancel(): void {
    const isSkip = this.isConsentRequestMode;
    const title = isSkip
      ? 'Are you sure you want to skip?'
      : 'Are you sure you want to cancel?';
    const message = isSkip
      ? "This will mean that your data won't be shared with a platform now. You can revisit this request later from the pending requests in your dashboard."
      : "This will mean that your data won't be shared with a platform.";

    this.utilService
      .openPopup(title, message, 'YesOrNo')
      .subscribe((result) => {
        if (result) {
          this.dialogRef.close({ cancelled: true });
          this.sdkManagerService.messageAndTerminateSession({
            status: 'FAILURE',
            message: 'REQUEST_CANCELLED',
          });
          this.logService.addUserActionLog(
            isSkip ? 'SKIP_SHARE' : 'CANCEL_SHARE',
          );
        }
      });
  }

  /**
   * Decline the consent request - updates notification status to DISMISSED
   */
  decline(): void {
    if (
      !this.isConsentRequestMode ||
      !this.data.consentRequest?.consentRequestId
    ) {
      return;
    }

    this.utilService
      .openPopup(
        'Decline Request?',
        'Are you sure you want to decline this sharing request? This action cannot be undone.',
        'YesOrNo',
      )
      .subscribe((result) => {
        if (result) {
          this.logService.addUserActionLog('DECLINE_SHARE');
          this.notificationService
            .updateNotificationStatus(
              this.data.consentRequest!.consentRequestId,
              NotificationStatus.DISMISSED,
            )
            .subscribe({
              next: (success) => {
                if (success) {
                  this.dialogRef.close({ declined: true });
                  this.sdkManagerService.messageAndTerminateSession({
                    status: 'FAILURE',
                    message: 'REQUEST_DECLINED',
                  });
                } else {
                  this.utilService.openSnackBar(
                    'Failed to decline request',
                    'Close',
                    'error',
                  );
                }
              },
              error: (error) => {
                console.error('Error declining consent request:', error);
                this.utilService.openSnackBar(
                  'Failed to decline request',
                  'Close',
                  'error',
                );
              },
            });
        }
      });
  }
}
